// ─────────────────────────────────────────────────────────────────────
// ConfiguratorPage — /configurer
// Composez votre accompagnement sur-mesure (à la Tesla/Porsche)
// DA Sabril · Audit Resto — ivoire · vert · or · Playfair · éditorial
// ─────────────────────────────────────────────────────────────────────

import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { consumeChantierMenu } from '../hooks/useChantierMenu';
import SalonPriveGate, { isSalonPriveUnlocked } from '../components/SalonPriveGate';
import {
  DA,
  SabrilDivider,
  AwningCorner,
  MimosaBranch,
  SabrilStamp,
  SabrilSignature,
} from '../theme/sabril-da';
import {
  Check,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Radio,
  Plane,
  MessageCircle,
  FileSignature,
  Sparkles,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_URL = 'https://wa.me/33745522450?text=Bonjour%20Sabril%2C%20je%20souhaite%20un%20RDV%20de%2030min%20pour%20d%C3%A9finir%20mes%20chantiers%20prioritaires.';

// ─── Chantier catalog — 18 (source: user brief 2026-02) ──────────────
// Each chantier has its own price. Chantier 11 (Réseaux sociaux) is
// bundled with 01 (ADN): selecting 11 auto-selects & locks 01.
const CHANTIERS = [
  { n: '01', title: 'ADN & Positionnement',           price: 1990, desc: 'Le socle qui fixe votre prix moyen et votre différenciation pour les 10 prochaines années.' },
  { n: '02', title: 'Identité visuelle & Marque',      price: 2490, desc: 'L\'image qui justifie +15 à 25 % de ticket moyen face à la concurrence.' },
  { n: '03', title: 'Design d\'espace & Expérience',  price: 2490, desc: 'L\'espace qui fait rester plus longtemps, revenir plus souvent, et poster sur Instagram gratuitement.' },
  { n: '04', title: 'Menu Gagnant',                   price: 1990, desc: '6 à 8 produits signatures rentables — +20 à 30 % de marge sur le menu en 60 jours.' },
  { n: '05', title: 'Merch · Création de lignes complète d\'objets', price: 2990, desc: 'Vos objets deviennent un actif de marque et un revenu additionnel — le client repart avec vous chez lui.' },
  { n: '06', title: 'Marges & Performance financière',price: 1990, desc: 'Récupérez 3 à 5 points de marge nette en 90 jours — coût matière, coût total, tableaux de bord simples et lisibles.' },
  { n: '07', title: 'Organisation du dirigeant',      price: 1690, desc: 'Passez de patron opérationnel à dirigeant — récupérez 15h par semaine pour développer votre business.' },
  { n: '08', title: 'Recrutement & Management',       price: 1690, desc: 'Une équipe autonome et fidèle — recrutement, intégration, encadrement structuré, plus jamais de trou dans le planning.' },
  { n: '09', title: 'Système de primes & Performance',price: 1290, desc: 'Un système de primes objectivé — motivation équipe = productivité +20 % mesurable.' },
  { n: '10', title: 'Expérience client & Fidélisation', price: 1490, desc: 'Le parcours qui transforme un client en ambassadeur — avis Google, réachat, bouche-à-oreille.' },
  { n: '11', title: 'DA & Stratégie réseaux sociaux', price: 2990, bundle: ['01'], desc: 'La direction artistique complète qui vous rend visible et désirable — cohérence marque à 360° (inclut ADN & Positionnement).' },
  { n: '12', title: 'Formation IA sur-mesure',          price: 1290, desc: 'Autonomie technologique — créez vos outils, contenus et automatisations vous-même.' },
  { n: '13', title: 'Intégration Resto 360',            price: 990,  desc: 'Setup complet et paramétrage de l\'app — inventaires, HACCP, équipes centralisés en 7 jours. Abonnement Resto 360 (349 €/mois) à la charge du client.' },
  { n: '14', title: 'Procédures & Modes d\'emploi',      price: 1290, desc: 'Vos procédures rédigées et déployées — qualité constante peu importe qui est en poste.' },
  { n: '15', title: 'HACCP & Affichages',               price: 890,  desc: 'Conformité réglementaire garantie — affichages, procédures cuisine, hygiène et traçabilité.' },
  { n: '16', title: 'Packaging',                        price: 890,  desc: 'Emballages take-away & livraison — votre marque cohérente jusque dans la main du client.' },
  { n: '17', title: 'Fiche technique',                  price: 790,  desc: 'Recettes standardisées avec coûts matière et marges — cuisine reproductible et industrialisable.' },
  { n: '18', title: 'Fiche de poste',                   price: 690,  desc: 'Chaque poste sait quoi faire — missions, objectifs, périmètre, recrutement structuré.' },
];

// Default per-chantier price (used when a chantier has no explicit `price`).
const CHANTIER_PRICE = 1290;   // € HT
const FULL_PACK_PRICE = 19990; // € HT — pack complet 360° (couvre les 18)
// Helpers
const priceOf = (c) => (typeof c?.price === 'number' ? c.price : CHANTIER_PRICE);
const priceOfN = (n) => priceOf(CHANTIERS.find((c) => c.n === n));

// ─── Bundles (3 packs recommandés + escape hatch sur-mesure) ─────────
// Le persona 40-60 ne veut pas cocher 18 cases. Il veut qu'on lui dise
// "voici le pack qu'il te faut" — 3 tiers clairs, économie affichée.
const BUNDLES = [
  {
    key: 'essentiels',
    label: 'Essentiels',
    subtitle: 'Les 5 leviers qui débloquent la rentabilité en 90 jours',
    chantiers: ['01', '04', '06', '08', '11'],
    price: 7490,
    badge: null,
  },
  {
    key: 'complet',
    label: 'Complet',
    subtitle: 'Toute ma méthode déployée sur votre restaurant',
    chantiers: ['01', '02', '03', '04', '05', '06', '07', '08', '10', '11', '12', '14'],
    price: 14990,
    badge: 'Le plus choisi',
  },
  {
    key: 'signature',
    label: 'Signature',
    subtitle: 'Absolument tout. Aucune option cachée.',
    chantiers: CHANTIERS.map((c) => c.n),
    price: 19990,
    badge: 'Tout inclus',
  },
];
const findBundle = (k) => BUNDLES.find((b) => b.key === k);
const bundleAlaCartePrice = (b) => b.chantiers.reduce((s, n) => s + priceOfN(n), 0);

// ─── Formats catalog ─────────────────────────────────────────────────
const FORMATS = [
  {
    key: 'distanciel',
    icon: Radio,
    label: '100 % à distance',
    duration: '90 jours',
    tagline: 'On travaille ensemble à distance pendant 3 mois — vous gardez votre resto ouvert, moi je vous coache chaque semaine.',
    priceMode: 'base_plus_chantiers',
    basePrice: 4990,
    highlight: 'Le plus flexible',
    forWho: 'Pour vous si vous êtes déjà ouvert et voulez avancer sans casser votre rythme.',
    included: [
      '3 mois de coaching stratégique · 1h30 en visio tous les 15 jours avec moi',
      'WhatsApp direct · je réponds sous 4h · pas de bot, pas de standardiste',
      'Feuille de route 90 jours · vos priorités chiffrées, planifiées, actionnables',
      'Bilan mensuel · on regarde ce qui a bougé, on ajuste',
    ],
  },
  {
    key: 'audit48',
    icon: Plane,
    label: 'Audit intensif 48h sur site',
    duration: '48h',
    tagline: 'Je débarque chez vous 2 jours. On plonge à fond dans votre resto et vous repartez avec un plan d\u2019action complet.',
    priceMode: 'base_plus_chantiers',
    basePrice: 2790,
    highlight: 'Résultats immédiats',
    onSiteNote: 'Pour la France, l\u2019Espagne et l\u2019Europe : le vol et l\u2019hébergement sont à votre charge. Le prix inclut mon déplacement, mon audit 48h sur place et le plan d\u2019action.',
    forWho: 'Pour vous si vous voulez un diagnostic complet, franc, et un plan d\u2019action clair sous 48h.',
    included: [
      '2 jours sur site avec moi · immersion totale',
      'Analyse à 360° · concept, chiffres, menu, marges, équipes, réseaux',
      'Rapport d\u2019audit complet · PDF signé remis à la fin',
      'Priorités classées de 1 à 10 · vous savez par où commencer',
      'Feuille de route 90 jours + gains financiers identifiés',
    ],
  },
  {
    key: 'hybride',
    icon: Sparkles,
    label: '90 jours dont 15 jours terrain',
    duration: '90 jours',
    tagline: 'Le format signature — 15 jours de présence terrain intégrés dans un suivi rapproché de 3 mois, avec les 12 chantiers inclus.',
    priceMode: 'flat_all_included',
    basePrice: 24990,
    highlight: 'Tout inclus · vol + hébergement compris',
    onSiteNote: 'Prix tout compris. Vol et hébergement du prestataire pris en charge par Sabril. Vous ne gérez aucune logistique — je viens, je m\u2019installe, je livre.',
    forWho: 'Pour vous si vous voulez qu\u2019on mette les mains dans le cambouis ensemble — ouverture, refonte ou virage stratégique.',
    included: [
      '15 jours de présence terrain · immersion complète sur site',
      '75 jours de suivi à distance · visio + WhatsApp prioritaire',
      'Les 12 chantiers stratégiques inclus · pas d\u2019option à ajouter',
      'Formation équipe sur place · management, service, hygiène',
      'Implémentation directe · pas juste de la théorie, on exécute',
      'Vol A/R + hébergement du prestataire · pris en charge par Sabril',
    ],
  },
];

const COUNTRIES = [
  { code: 'MA', flag: '🇲🇦', label: 'Maroc',   price: 2190 },
  { code: 'FR', flag: '🇫🇷', label: 'France',  price: 2790 },
  { code: 'ES', flag: '🇪🇸', label: 'Espagne', price: 2790 },
  { code: 'EU', flag: '🇪🇺', label: 'Europe',  price: 2790 },
];

// ─── Compact number formatter ────────────────────────────────────────
const fmt = (n) => new Intl.NumberFormat('fr-FR').format(n);

export { CHANTIERS, FORMATS, CHANTIER_PRICE, FULL_PACK_PRICE, priceOf, priceOfN, fmt };

// ─── AnimatedPrice — smooth count-up + gold flash when value changes (Tesla configurator feel) ─
function AnimatedPrice({ value, duration = 480, className, style, prefix = '', suffix = ' €' }) {
  const [display, setDisplay] = useState(value);
  const [pulsing, setPulsing] = useState(false);
  const prevRef = useRef(value);
  const flashTimerRef = useRef(null);
  useEffect(() => {
    const from = prevRef.current;
    const to = value;
    if (from === to) return undefined;
    // Trigger gold flash
    setPulsing(true);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setPulsing(false), 520);
    // Count-up animation
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      const current = Math.round(from + (to - from) * eased);
      setDisplay(current);
      if (t < 1) raf = requestAnimationFrame(tick);
      else prevRef.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
  }, [value, duration]);
  return (
    <span
      className={className}
      style={{
        fontVariantNumeric: 'tabular-nums',
        transition: 'text-shadow 240ms ease-out, color 240ms ease-out',
        textShadow: pulsing
          ? '0 0 12px rgba(216,148,64,0.65), 0 0 22px rgba(216,148,64,0.35)'
          : '0 0 0 rgba(216,148,64,0)',
        ...style,
      }}
      data-testid="animated-price"
      data-pulsing={pulsing ? '1' : '0'}
    >
      {prefix}{fmt(display)}{suffix}
    </span>
  );
}

// ═════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═════════════════════════════════════════════════════════════════════
export default function ConfiguratorPage() {
  const navigate = useNavigate();
  // ── PREMIUM GATE — private access via code delivered on Instagram (@sabril.j) ──
  const [unlocked, setUnlocked] = useState(() => isSalonPriveUnlocked());
  const [step, setStep] = useState(1);
  const [format, setFormat] = useState('distanciel');
  const [country, setCountry] = useState('MA');
  const [selected, setSelected] = useState([]);
  const [fullPack, setFullPack] = useState(false);
  // Bundle: 'essentiels' | 'complet' | 'signature' | 'custom' | null (rien choisi)
  const [bundleKey, setBundleKey] = useState(null);
  const [contact, setContact] = useState({ firstName: '', email: '', phone: '', restaurant: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [expandedDesc, setExpandedDesc] = useState(new Set());

  const toggleDesc = (n) => {
    setExpandedDesc((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n); else next.add(n);
      return next;
    });
  };

  const toggleAllDesc = (open) => {
    setExpandedDesc(open ? new Set(CHANTIERS.map((c) => c.n)) : new Set());
  };

  // Reset fullPack when switching to a format where Pack complet isn't offered
  // (Pack complet 12 900€ is a distanciel-only concept). Without this reset,
  // fullPack leaks into audit48/hybride and locks all chantier checkboxes.
  useEffect(() => {
    if (format !== 'distanciel') {
      setFullPack(false);
    }
  }, [format]);

  // Handoff from /prsnl "Ajouter au menu": pre-select the chantiers the user
  // added to their menu while browsing. Land on Distanciel by default and
  // scroll into the format card so they see their selection right away.
  useEffect(() => {
    const items = consumeChantierMenu();
    if (items.length === 0) return;
    const valid = items.filter((n) => CHANTIERS.some((c) => c.n === n));
    if (!valid.length) return;
    setSelected(valid);
    setFormat('distanciel');
    setFullPack(false);
    // Nudge the user with a toast confirming their menu was applied
    setTimeout(() => {
      toast.success(
        `Menu appliqué · ${valid.length} chantier${valid.length > 1 ? 's' : ''} pré-sélectionné${valid.length > 1 ? 's' : ''}`,
        { duration: 3500 }
      );
    }, 400);
  }, []); // once on mount

  const currentFormat = FORMATS.find((f) => f.key === format);

  // Sum of prices of the currently selected chantiers (per-chantier pricing).
  const selectedSum = useMemo(
    () => selected.reduce((s, n) => s + priceOfN(n), 0),
    [selected]
  );

  // Sum of ALL chantier prices (used for pack savings math)
  const allChantiersSum = useMemo(
    () => CHANTIERS.reduce((s, c) => s + priceOf(c), 0),
    []
  );

  // ─── Price computation ─────────────────────────────────────────────
  const { total, priceLabel, isQuote } = useMemo(() => {
    // Hybride is a flat "tout inclus" price — no chantier selection needed
    if (currentFormat.priceMode === 'flat_all_included') {
      return { total: currentFormat.basePrice, priceLabel: `${fmt(currentFormat.basePrice)} € HT`, isQuote: false };
    }
    if (currentFormat.priceMode === 'quote') {
      return { total: currentFormat.basePrice || 0, priceLabel: 'Sur devis', isQuote: true };
    }
    // Bundle sélectionné (essentiels / complet / signature) — prix flat pack
    const bundle = bundleKey && bundleKey !== 'custom' ? findBundle(bundleKey) : null;
    if (bundle) {
      const base = currentFormat.priceByCountry?.[country] || currentFormat.basePrice || 0;
      const t = base + bundle.price;
      return { total: t, priceLabel: `${fmt(t)} € HT`, isQuote: false };
    }
    // Legacy Pack Complet 360° (rare — laissé pour rétro-compat)
    if (fullPack) {
      return { total: FULL_PACK_PRICE, priceLabel: `${fmt(FULL_PACK_PRICE)} € HT`, isQuote: false };
    }
    // Sur-mesure (custom) OU aucun pack choisi
    if (currentFormat.priceMode === 'per_chantier') {
      return { total: selectedSum, priceLabel: `${fmt(selectedSum)} € HT`, isQuote: false };
    }
    if (currentFormat.priceMode === 'base_plus_chantiers') {
      const base = currentFormat.priceByCountry?.[country] || currentFormat.basePrice;
      const t = base + selectedSum;
      return { total: t, priceLabel: `${fmt(t)} € HT`, isQuote: false };
    }
    return { total: 0, priceLabel: '—', isQuote: false };
  }, [currentFormat, selectedSum, country, fullPack, bundleKey]);

  const savings = useMemo(() => {
    if (fullPack || currentFormat.priceMode !== 'per_chantier') return 0;
    return allChantiersSum - FULL_PACK_PRICE;
  }, [fullPack, currentFormat, allChantiersSum]);

  // Bundling rules: chantier 11 (DA & Stratégie réseaux sociaux) auto-includes
  // 01 (ADN & Positionnement) and locks it while 11 is active.
  const isBundleLocked = (n) => {
    // 01 is locked when 11 is selected
    if (n === '01' && selected.includes('11')) return true;
    return false;
  };

  const toggleChantier = (n) => {
    if (fullPack) return;
    if (isBundleLocked(n)) return; // silently ignore taps on locked bundle items
    setSelected((prev) => {
      if (prev.includes(n)) {
        // Removing chantier 11 also removes any bundle-locked-only inclusion of 01?
        // No — 01 stays if user had it before. Just remove 11.
        return prev.filter((x) => x !== n);
      }
      // Adding: also add bundled deps (e.g. 11 → auto-add 01)
      const chantier = CHANTIERS.find((c) => c.n === n);
      const additions = [n];
      if (chantier?.bundle?.length) {
        chantier.bundle.forEach((b) => {
          if (!prev.includes(b) && !additions.includes(b)) additions.push(b);
        });
      }
      return [...prev, ...additions];
    });
  };

  const canSubmit = contact.firstName && contact.email && contact.phone && contact.restaurant;

  const handleSubmit = async (mode) => {
    if (!canSubmit) {
      toast.error('Merci de renseigner vos coordonnées.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        format: currentFormat.key,
        format_label: currentFormat.label,
        country: format === 'audit48' ? country : null,
        chantiers: fullPack ? CHANTIERS.map((c) => c.n) : selected,
        full_pack: fullPack,
        bundle_key: bundleKey && bundleKey !== 'custom' ? bundleKey : null,
        total_ht: isQuote ? null : total,
        contact,
        mode, // 'quote' | 'contract'
      };
      const res = await fetch(`${BACKEND_URL}/api/configurator/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('server_error');
      const data = await res.json();

      if (mode === 'contract' && data.contract_url) {
        // Persist the configuration for the contract page to pre-fill the modal.
        try {
          sessionStorage.setItem('sabril_config_prefill', JSON.stringify({
            format: payload.format,
            format_label: payload.format_label,
            country: payload.country,
            chantiers: payload.chantiers,
            full_pack: payload.full_pack,
            bundle_key: payload.bundle_key,
            total_ht: payload.total_ht,
            contact: payload.contact,
            lead_id: data.lead_id,
            createdAt: Date.now(),
          }));
        } catch (_e) { /* sessionStorage may be blocked — proceed anyway */ }
        toast.success('Récapitulatif enregistré · direction contrat.');
        navigate(data.contract_url);
        return;
      }

      toast.success('Demande envoyée · je vous réponds sous 24h.');
      setStep(4); // success step
    } catch (e) {
      toast.error('Une erreur est survenue. Contactez-moi directement sur WhatsApp.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    unlocked ? (
    <div
      className="min-h-screen w-full relative overflow-x-hidden"
      style={{ background: DA.ivory, fontFamily: DA.sans }}
      data-testid="configurator-page"
    >
      {/* Global decorative background (top-left retiré : masquait le bouton "Retour /prsnl") */}
      <div className="pointer-events-none fixed inset-0" style={{ zIndex: 0 }}>
        <AwningCorner position="bottom-right" size={220} opacity={0.85} />
        <MimosaBranch size={200} position="bottom-left" opacity={0.6} />
      </div>

      <div className="relative max-w-3xl mx-auto px-5 py-10 sm:py-14" style={{ zIndex: 1 }}>
        {/* ══ TOP BRAND ══ */}
        <Header onBack={() => navigate('/prsnl')} />

        {/* ══ SUCCESS STATE — takes over the page ══ */}
        {step === 4 ? (
          <StepSuccess onBack={() => navigate('/prsnl')} />
        ) : (
          <>
            {/* ══ SECTION 1 · FORMAT (contient les chantiers inline + bouton Suivant dans chaque carte active) ══ */}
            <StepFormat
              format={format}
              setFormat={setFormat}
              country={country}
              setCountry={setCountry}
              selected={selected}
              toggleChantier={toggleChantier}
              fullPack={fullPack}
              setFullPack={setFullPack}
              bundleKey={bundleKey}
              setBundleKey={setBundleKey}
              setSelected={setSelected}
              total={total}
              expandedDesc={expandedDesc}
              toggleDesc={toggleDesc}
              toggleAllDesc={toggleAllDesc}
            />

            {/* ══ FLOATING HELP CTA ══ */}
            <HelpCTA />
          </>
        )}
      </div>
    </div>
    ) : (
      <SalonPriveGate onUnlocked={() => setUnlocked(true)} />
    )
  );
}

// ─────────────────────────────────────────────────────────────────────
// HEADER — Premium authority hero (redesigned for 40+ dirigeant persona)
// ─────────────────────────────────────────────────────────────────────
const SABRIL_AVATAR_URL = 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/xf03ot6r_sabril.png';

function Header({ onBack }) {
  return (
    <div className="mb-6">
      {/* Retour — discret en haut */}
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1.5 mb-6"
        style={{
          fontFamily: DA.mono, fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase',
          color: DA.inkMuted, fontWeight: 700,
        }}
        data-testid="config-back-home"
      >
        <ChevronLeft size={12} strokeWidth={2.2} />
        Retour /prsnl
      </button>

      {/* Signature bloc — avatar + authority claim */}
      <div className="flex items-center gap-3 mb-5" data-testid="config-signature-block">
        <img
          src={SABRIL_AVATAR_URL}
          alt="Sabril Jurczeszyn"
          style={{
            width: 56, height: 56, borderRadius: 999,
            objectFit: 'cover',
            border: `2px solid ${DA.goldMain}`,
            boxShadow: `0 8px 22px -8px rgba(216,148,64,0.55), 0 0 0 4px ${DA.paper}`,
            flexShrink: 0,
          }}
        />
        <div>
          <p style={{
            fontFamily: DA.mono, fontSize: 9.5, fontWeight: 800,
            letterSpacing: '0.24em', textTransform: 'uppercase',
            color: DA.goldDeep, marginBottom: 3,
          }}>
            Par Sabril Jurczeszyn
          </p>
          <p style={{
            fontFamily: DA.sans, fontSize: 12, fontWeight: 600,
            color: DA.greenDeep, lineHeight: 1.3, letterSpacing: '-0.01em',
          }}>
            Directeur associé · 10&nbsp;ans dans la restauration<br/>
            <span style={{ color: DA.inkMuted, fontWeight: 500 }}>Vous parlez à moi, pas à un commercial.</span>
          </p>
        </div>
      </div>

      {/* Confidential seal — arrival mark after the manifesto */}
      <div
        className="flex items-center justify-between mb-6 px-3 py-2"
        style={{
          background: DA.paper,
          border: `0.5px solid ${DA.goldSoft}`,
          borderRadius: 999,
        }}
        data-testid="config-confidential-seal"
      >
        <span style={{
          fontFamily: DA.mono, fontSize: 8.5, fontWeight: 800,
          letterSpacing: '0.28em', textTransform: 'uppercase',
          color: DA.goldDeep,
        }}>
          Salon Privé · Confidentiel
        </span>
        <span style={{
          fontFamily: DA.mono, fontSize: 8.5, fontWeight: 700,
          letterSpacing: '0.28em', textTransform: 'uppercase',
          color: DA.inkMuted,
        }}>
          Édition 2026 · Nº 01
        </span>
      </div>

      {/* Headline — punchy, action-first */}
      <h1 style={{
        fontFamily: DA.serif,
        fontSize: 'clamp(28px, 5.5vw, 44px)',
        fontWeight: 900,
        letterSpacing: '-0.03em',
        color: DA.greenDeep,
        lineHeight: 1.04,
        marginBottom: 12,
      }}>
        Votre restaurant mérite<br/>
        <span style={{ color: DA.goldDeep, fontStyle: 'italic' }}>un accompagnement sur-mesure.</span>
      </h1>

      {/* Sub — feutré Hermès */}
      <p style={{
        fontFamily: DA.sans, fontSize: 14, color: DA.inkBody,
        lineHeight: 1.5, letterSpacing: '-0.005em', marginBottom: 18,
      }}>
        Format, chantiers, dossier de candidature — <strong style={{ color: DA.greenDeep }}>en moins de 3 minutes</strong>.
      </p>

      {/* Trust pills — 3 chips horizontaux */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="config-trust-pills">
        {[
          { n: '18', label: 'chantiers stratégiques' },
          { n: '3',  label: 'formats sur-mesure' },
          { n: '4',  label: 'places par trimestre' },
        ].map((p, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1.5"
            style={{
              background: DA.paper,
              border: `0.5px solid ${DA.goldSoft}`,
              borderRadius: 999,
              padding: '5px 11px 5px 6px',
              boxShadow: '0 1px 2px rgba(11,43,36,0.04)',
            }}
          >
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              minWidth: 22, height: 22, borderRadius: 999,
              background: DA.greenDeep, color: DA.goldMain,
              fontFamily: DA.mono, fontSize: 10, fontWeight: 900,
              padding: '0 5px',
            }}>{p.n}</span>
            <span style={{
              fontFamily: DA.sans, fontSize: 11, fontWeight: 700,
              color: DA.greenDeep, letterSpacing: '-0.005em',
            }}>{p.label}</span>
          </span>
        ))}
      </div>

      {/* Petit disclaimer HT — remplace le gros bloc d'avant */}
      <p style={{
        fontFamily: DA.mono, fontSize: 8.5, fontWeight: 700,
        letterSpacing: '0.20em', textTransform: 'uppercase',
        color: DA.inkFaint,
      }}>
        Prix affichés HT · TVA 20% en sus
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// STEPPER
// ─────────────────────────────────────────────────────────────────────
function Stepper({ current, onJump }) {
  const steps = [
    { n: 1, label: 'Format' },
    { n: 2, label: 'Chantiers' },
    { n: 3, label: 'Récapitulatif' },
  ];
  return (
    <div className="flex items-center justify-center gap-3 mb-8" data-testid="config-stepper">
      {steps.map((s, i) => {
        const active = current === s.n;
        const done = current > s.n;
        return (
          <div key={s.n} className="flex items-center gap-3">
            <button
              onClick={() => onJump(s.n)}
              className="flex items-center gap-2 transition-opacity"
              style={{ opacity: active || done ? 1 : 0.4, cursor: done ? 'pointer' : 'default' }}
              data-testid={`stepper-${s.n}`}
            >
              <span
                className="inline-flex items-center justify-center flex-shrink-0"
                style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: active ? DA.greenDeep : done ? DA.goldMain : 'transparent',
                  border: active || done ? 'none' : `0.5px solid ${DA.goldSoft}`,
                  color: active || done ? '#FFF' : DA.inkMuted,
                  fontFamily: DA.mono, fontSize: 11, fontWeight: 800,
                }}
              >
                {done ? <Check size={12} strokeWidth={2.8} /> : s.n}
              </span>
              <span style={{
                fontFamily: DA.mono, fontSize: 10, letterSpacing: '0.22em',
                textTransform: 'uppercase', fontWeight: 700,
                color: active ? DA.greenDeep : DA.inkMuted,
              }}>
                {s.label}
              </span>
            </button>
            {i < steps.length - 1 && (
              <span style={{ width: 20, height: 0.5, background: DA.goldSoft }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// STEP 1 — FORMAT
// ─────────────────────────────────────────────────────────────────────
export function StepFormat({
  format, setFormat, country, setCountry, onNext,
  // Inline chantiers integration
  selected, toggleChantier, fullPack, setFullPack, total,
  // Bundles (NEW)
  bundleKey, setBundleKey, setSelected,
  // Description expansion
  expandedDesc, toggleDesc, toggleAllDesc,
  // UX
  onSubmit, submitting, contact, setContact,
  hideHelp,
}) {
  // Bundle rule: 01 is locked (auto-included) when 11 is selected
  const isBundleLocked = (n) => n === '01' && selected.includes('11');
  const [hybrideDetailOpen, setHybrideDetailOpen] = useState(false);
  const selectedSum = selected.reduce((s, n) => {
    const c = CHANTIERS.find((x) => x.n === n);
    return s + (c ? priceOf(c) : CHANTIER_PRICE);
  }, 0);
  return (
    <div className="animate-fadein" data-testid="step-format">
      {/* ══ COMMENT ÇA MARCHE — mini-schéma 3 étapes ══ */}
      <div className="mb-4" data-testid="how-it-works-schema">
        <p style={{
          fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
          letterSpacing: '0.24em', textTransform: 'uppercase',
          color: DA.goldDeep, marginBottom: 10,
        }}>
          Le parcours en 3 temps
        </p>
        <div className="flex flex-col gap-2">
          {[
            {
              n: '1',
              title: 'Vous choisissez votre format',
              desc: '100% à distance, 48h sur site, ou 15 jours d\'immersion — le cadre dans lequel nous travaillerons ensemble.',
              icon: '⚙',
            },
            {
              n: '2',
              title: 'Vous ajoutez vos chantiers',
              desc: 'Marges, positionnement, menu, équipe... Vous sélectionnez les leviers stratégiques que vous souhaitez activer.',
              icon: '🎯',
              highlight: true,
              highlightTitle: 'Sabril & son équipe exécutent',
              highlightDesc: 'Vous ne recevez pas un simple plan. Sabril et ses équipes produisent, livrent et incarnent chaque chantier à vos côtés.',
            },
            {
              n: '3',
              title: 'Vous postulez à la liste d\'attente',
              desc: 'Nous étudions chaque candidature personnellement. Sabril vous recontacte pour valider votre entrée dans le Salon Privé.',
              icon: '✍',
            },
          ].map((step) => (
            <div
              key={step.n}
              className="flex items-start gap-3 p-3"
              style={{
                background: step.highlight ? DA.greenDeep : DA.paper,
                border: `${step.highlight ? 1 : 0.5}px solid ${step.highlight ? DA.goldMain : DA.goldSoft}`,
                borderRadius: 12,
                boxShadow: step.highlight
                  ? '0 10px 26px -12px rgba(11,43,36,0.35)'
                  : '0 1px 2px rgba(11,43,36,0.03)',
              }}
            >
              {/* Number badge */}
              <span
                className="flex-shrink-0 inline-flex items-center justify-center"
                style={{
                  width: 28, height: 28, borderRadius: 999,
                  background: step.highlight ? DA.goldMain : DA.greenDeep,
                  color: step.highlight ? DA.greenDeep : DA.goldMain,
                  fontFamily: DA.mono, fontSize: 13, fontWeight: 900,
                  marginTop: 1,
                }}
              >
                {step.n}
              </span>

              <div className="flex-1 min-w-0">
                <p style={{
                  fontFamily: DA.serif, fontSize: 14, fontWeight: 800,
                  color: step.highlight ? '#FFFFFF' : DA.greenDeep,
                  letterSpacing: '-0.01em', lineHeight: 1.25,
                }}>
                  {step.title}
                </p>
                <p style={{
                  marginTop: 3,
                  fontFamily: DA.sans, fontSize: 11.5,
                  color: step.highlight ? 'rgba(255,255,255,0.82)' : DA.inkBody,
                  lineHeight: 1.4, letterSpacing: '-0.005em',
                }}>
                  {step.desc}
                </p>

                {/* Highlight sub-bloc — "Sabril & son équipe exécutent" */}
                {step.highlight && (
                  <div
                    className="mt-2.5 pt-2.5 pl-3"
                    style={{
                      borderTop: '0.5px solid rgba(216,148,64,0.35)',
                      borderLeft: `2px solid ${DA.goldMain}`,
                    }}
                  >
                    <p style={{
                      fontFamily: DA.mono, fontSize: 8.5, letterSpacing: '0.22em',
                      textTransform: 'uppercase', color: DA.goldMain, fontWeight: 800,
                      marginBottom: 3,
                    }}>
                      ⚡ Le point-clé
                    </p>
                    <p style={{
                      fontFamily: DA.serif, fontSize: 12.5, fontWeight: 800,
                      color: '#FFFFFF', letterSpacing: '-0.005em', lineHeight: 1.3,
                    }}>
                      {step.highlightTitle}
                    </p>
                    <p style={{
                      marginTop: 3,
                      fontFamily: DA.sans, fontSize: 11,
                      color: 'rgba(255,255,255,0.86)',
                      fontStyle: 'italic', lineHeight: 1.42,
                    }}>
                      {step.highlightDesc}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ══ SINGLE UNIFIED CARD ══ */}
      <div
        className="mt-2"
        style={{
          background: DA.paper,
          borderRadius: 22,
          border: `0.5px solid ${DA.goldSoft}`,
          boxShadow: '0 24px 60px -18px rgba(11,43,36,0.20), 0 2px 8px -2px rgba(11,43,36,0.08)',
          padding: '18px 16px 20px',
        }}
        data-testid="config-single-card"
      >
        {/* ── Format tabs — 3 pills at the top ── */}
        <p style={{
          fontFamily: DA.mono, fontSize: 9.5, letterSpacing: '0.24em',
          textTransform: 'uppercase', color: DA.goldDeep, fontWeight: 800,
          marginBottom: 10,
        }}>
          Formule
        </p>
        <div
          className="grid grid-cols-3 gap-2"
          role="tablist"
          aria-label="Choix du format"
        >
          {FORMATS.map((f) => {
            const Icon = f.icon;
            const active = format === f.key;
            const shortLabel = f.key === 'distanciel'
              ? '100 % distance'
              : f.key === 'audit48'
                ? '48h express'
                : '15j sur place';
            return (
              <button
                key={f.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setFormat(f.key)}
                data-testid={`format-${f.key}`}
                className="flex flex-col items-center justify-center text-center transition-all active:scale-[0.97]"
                style={{
                  background: active
                    ? `linear-gradient(180deg, ${DA.greenDeep} 0%, #0F3A31 100%)`
                    : DA.ivoryLight,
                  color: active ? DA.ivory : DA.greenDeep,
                  borderRadius: 14,
                  padding: '11px 6px 10px',
                  border: `${active ? 1 : 0.5}px solid ${active ? DA.goldMain : DA.goldSoft}`,
                  boxShadow: active
                    ? '0 8px 20px -8px rgba(11,43,36,0.45), inset 0 1px 0 rgba(216,148,64,0.35)'
                    : 'none',
                  cursor: 'pointer',
                  minHeight: 108,
                  gap: 3,
                }}
              >
                <Icon
                  size={18}
                  strokeWidth={active ? 2 : 1.7}
                  style={{ color: active ? DA.goldMain : DA.greenDeep }}
                />
                <span style={{
                  fontFamily: DA.sans, fontSize: 11.5, fontWeight: 700,
                  letterSpacing: '-0.005em',
                  color: active ? DA.ivory : DA.greenDeep,
                  lineHeight: 1.1,
                }}>
                  {shortLabel}
                </span>
                <span style={{
                  fontFamily: DA.mono, fontSize: 8.5, fontWeight: 700,
                  letterSpacing: '0.14em', textTransform: 'uppercase',
                  color: active ? 'rgba(255,255,255,0.55)' : DA.inkFaint,
                  lineHeight: 1,
                }}>
                  {f.duration}
                </span>
                {/* Prix — visible AVANT le clic pour éliminer la friction cognitive */}
                <div
                  className="mt-1 px-2 py-1"
                  style={{
                    background: active ? 'rgba(216,148,64,0.16)' : DA.paper,
                    border: `0.5px solid ${active ? DA.goldMain : DA.goldSoft}`,
                    borderRadius: 8,
                  }}
                >
                  <span style={{
                    fontFamily: DA.mono, fontSize: 7.5, fontWeight: 800,
                    letterSpacing: '0.18em', textTransform: 'uppercase',
                    color: active ? DA.goldMain : DA.inkMuted,
                    display: 'block', lineHeight: 1,
                  }}>
                    {f.priceMode === 'flat_all_included' ? 'Tout inclus' : 'À partir de'}
                  </span>
                  <span style={{
                    fontFamily: DA.sans, fontSize: 13, fontWeight: 900,
                    letterSpacing: '-0.02em',
                    color: active ? DA.goldMain : DA.greenDeep,
                    display: 'block', lineHeight: 1.15, marginTop: 2,
                    whiteSpace: 'nowrap',
                  }}>
                    {fmt(f.basePrice)}&nbsp;€
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* ── Selected format meta ── */}
        <div className="mt-4 pt-4" style={{ borderTop: `0.5px solid ${DA.goldSoft}` }}>
          {(() => {
            const f = FORMATS.find((x) => x.key === format) || FORMATS[0];
            return (
              <div>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 style={{
                      fontFamily: DA.serif, fontSize: 18, fontWeight: 900,
                      color: DA.greenDeep, letterSpacing: '-0.02em', lineHeight: 1.15,
                    }}>
                      {f.label}
                    </h3>
                    <p style={{
                      fontFamily: DA.sans, fontSize: 12.5, color: DA.inkMuted,
                      lineHeight: 1.5, marginTop: 4,
                    }}>
                      {f.tagline}
                    </p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span style={{
                        fontFamily: DA.sans, fontSize: 11, color: DA.inkFaint, fontStyle: 'italic',
                      }}>
                        {f.highlight}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Pour qui — quick hook line */}
                {f.forWho && (
                  <div
                    className="mt-3 flex items-start gap-2 px-3 py-2.5"
                    style={{
                      background: 'rgba(216,148,64,0.08)',
                      border: `0.5px solid ${DA.goldSoft}`,
                      borderRadius: 10,
                    }}
                    data-testid={`format-forwho-${f.key}`}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={DA.goldDeep} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2, flexShrink: 0 }} aria-hidden>
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    <p style={{
                      fontFamily: DA.sans, fontSize: 11.5, color: DA.greenDeep,
                      lineHeight: 1.5, letterSpacing: '-0.005em',
                    }}>
                      {f.forWho}
                    </p>
                  </div>
                )}

                {/* VOTRE ACCOMPAGNEMENT DE BASE — clarification pour le client */}
                {f.included?.length > 0 && (
                  <div className="mt-3" data-testid={`format-included-${f.key}`}>
                    <p style={{
                      fontFamily: DA.mono, fontSize: 9, letterSpacing: '0.24em',
                      textTransform: 'uppercase', color: DA.goldDeep, fontWeight: 800,
                      marginBottom: 4,
                    }}>
                      Votre accompagnement de base
                    </p>
                    <p style={{
                      fontFamily: DA.sans, fontSize: 11, color: DA.inkMuted,
                      fontStyle: 'italic', marginBottom: 10, lineHeight: 1.4,
                    }}>
                      Ce que Sabril &amp; son équipe vous livrent pour {fmt(f.basePrice)} € HT,
                      {f.priceMode === 'flat_all_included' ? ' avec les 12 chantiers déjà inclus' : ' avant même d\'ajouter un chantier'}.
                    </p>
                    <ul className="flex flex-col gap-1.5">
                      {f.included.map((it, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2"
                          style={{
                            fontFamily: DA.sans, fontSize: 12.5,
                            color: DA.greenDeep, lineHeight: 1.4,
                            letterSpacing: '-0.005em',
                          }}
                        >
                          <span
                            className="inline-flex items-center justify-center flex-shrink-0"
                            style={{
                              width: 15, height: 15, borderRadius: 999,
                              background: DA.goldMain,
                              color: '#FFFFFF',
                              marginTop: 2,
                            }}
                            aria-hidden
                          >
                            <Check size={9} strokeWidth={3.4} />
                          </span>
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Production distinction — clarify base vs chantiers */}
                    {f.priceMode !== 'flat_all_included' && (
                      <div
                        className="mt-4 p-3 flex items-start gap-2.5"
                        style={{
                          background: DA.greenDeep,
                          border: `0.5px solid ${DA.goldMain}`,
                          borderRadius: 10,
                        }}
                        data-testid={`production-clarify-${f.key}`}
                      >
                        <span
                          className="flex-shrink-0 inline-flex items-center justify-center"
                          style={{
                            width: 24, height: 24, borderRadius: 6,
                            background: DA.goldMain, color: DA.greenDeep,
                            fontFamily: DA.mono, fontSize: 12, fontWeight: 900,
                            marginTop: 1,
                          }}
                        >
                          🔧
                        </span>
                        <div style={{ flex: 1 }}>
                          <p style={{
                            fontFamily: DA.mono, fontSize: 8.5, letterSpacing: '0.24em',
                            textTransform: 'uppercase', color: DA.goldMain, fontWeight: 800,
                            marginBottom: 4,
                          }}>
                            Production &amp; exécution
                          </p>
                          <p style={{
                            fontFamily: DA.sans, fontSize: 12,
                            color: '#FFFFFF', lineHeight: 1.5,
                            letterSpacing: '-0.005em',
                          }}>
                            Ci-dessus, votre <strong>coaching stratégique</strong>. Pour la <strong>production concrète</strong> (design, menu, marges, HACCP...), ajoutez les chantiers ci-dessous — <em style={{ color: DA.goldMain, fontStyle: 'normal', fontWeight: 700 }}>Sabril et son équipe les exécutent avec vous</em>.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* ── DÉTAIL DES 12 CHANTIERS · Format Hybride seulement (devis-style) ── */}
                    {f.key === 'hybride' && (
                      <div className="mt-4" data-testid="hybride-chantiers-detail">
                        <button
                          type="button"
                          onClick={() => setHybrideDetailOpen((v) => !v)}
                          aria-expanded={hybrideDetailOpen}
                          data-testid="hybride-chantiers-toggle"
                          className="w-full flex items-center justify-between gap-2 py-2.5 px-3 active:scale-[0.99] transition-all"
                          style={{
                            background: hybrideDetailOpen ? DA.greenDeep : DA.paper,
                            color: hybrideDetailOpen ? '#FFFFFF' : DA.greenDeep,
                            border: `1px solid ${DA.goldMain}`,
                            borderRadius: 10,
                            fontFamily: DA.sans,
                            fontSize: 12,
                            fontWeight: 800,
                            letterSpacing: '0.02em',
                            cursor: 'pointer',
                          }}
                        >
                          <span className="flex items-center gap-2">
                            <span style={{
                              display: 'inline-flex', width: 20, height: 20, borderRadius: 6,
                              background: DA.goldMain, color: '#FFFFFF',
                              alignItems: 'center', justifyContent: 'center',
                              fontFamily: DA.mono, fontSize: 10, fontWeight: 900,
                            }}>18</span>
                            {hybrideDetailOpen ? 'Masquer' : 'Voir'} le détail des 12 chantiers inclus
                          </span>
                          <ChevronRight
                            size={16}
                            strokeWidth={2.6}
                            style={{
                              transform: hybrideDetailOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                              transition: 'transform 220ms cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                          />
                        </button>

                        <AnimatePresence initial={false}>
                          {hybrideDetailOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
                              style={{ overflow: 'hidden' }}
                            >
                              <div
                                className="mt-2 p-4"
                                style={{
                                  background: DA.ivoryLight,
                                  border: `0.5px solid ${DA.goldSoft}`,
                                  borderRadius: 12,
                                }}
                              >
                                <p style={{
                                  fontFamily: DA.mono, fontSize: 8.5, letterSpacing: '0.24em',
                                  textTransform: 'uppercase', color: DA.goldDeep, fontWeight: 800,
                                  marginBottom: 10,
                                }}>
                                  Détail façon devis · les 18 leviers déployés sur votre restaurant
                                </p>
                                <ul className="flex flex-col gap-3">
                                  {CHANTIERS.map((c) => (
                                    <li key={c.n} className="flex items-start gap-2.5">
                                      <span
                                        className="inline-flex items-center justify-center flex-shrink-0"
                                        style={{
                                          width: 22, height: 22, borderRadius: 6,
                                          background: DA.greenDeep, color: DA.goldMain,
                                          fontFamily: DA.mono, fontSize: 10, fontWeight: 900,
                                          marginTop: 1,
                                        }}
                                        aria-hidden
                                      >
                                        {c.n}
                                      </span>
                                      <div style={{ flex: 1 }}>
                                        <p style={{
                                          fontFamily: DA.serif, fontSize: 13.5, fontWeight: 700,
                                          color: DA.greenDeep, lineHeight: 1.25,
                                          letterSpacing: '-0.005em',
                                        }}>
                                          {c.title}
                                        </p>
                                        {c.desc && (
                                          <p style={{
                                            marginTop: 3,
                                            fontFamily: DA.sans, fontSize: 11.5,
                                            fontStyle: 'italic',
                                            color: DA.inkBody,
                                            lineHeight: 1.45,
                                          }}>
                                            {c.desc}
                                          </p>
                                        )}
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                                <div
                                  className="mt-4 pt-3 flex items-center justify-between"
                                  style={{ borderTop: `0.5px dashed ${DA.goldSoft}` }}
                                >
                                  <span style={{
                                    fontFamily: DA.mono, fontSize: 9, letterSpacing: '0.22em',
                                    textTransform: 'uppercase', color: DA.inkMuted, fontWeight: 700,
                                  }}>
                                    Valeur à l&apos;unité
                                  </span>
                                  <span style={{
                                    fontFamily: DA.sans, fontSize: 14, fontWeight: 900,
                                    color: DA.greenDeep,
                                  }}>
                                    <span style={{ textDecoration: 'line-through', color: DA.inkFaint, fontWeight: 600, marginRight: 6 }}>
                                      {fmt(CHANTIERS.reduce((s, c) => s + priceOf(c), 0))} €
                                    </span>
                                    {fmt(f.basePrice)} €&nbsp;HT
                                  </span>
                                </div>
                                <p style={{
                                  marginTop: 8,
                                  fontFamily: DA.sans, fontSize: 10.5, fontStyle: 'italic',
                                  color: DA.goldDeep, textAlign: 'center', lineHeight: 1.5,
                                }}>
                                  Économie {fmt(Math.max(0, CHANTIERS.reduce((s, c) => s + priceOf(c), 0) - f.basePrice))} € HT + 15j de terrain + vol/hébergement offerts.
                                </p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* ── On-site fees note (audit48 + hybride) — replaces country dropdown ── */}
        {(() => {
          const f = FORMATS.find((x) => x.key === format);
          if (!f?.onSiteNote) return null;
          return (
            <div className="mt-4 pt-4" style={{ borderTop: `0.5px solid ${DA.goldSoft}` }}>
              <div
                className="flex items-start gap-2 px-3 py-2.5"
                style={{
                  background: DA.ivoryDeep,
                  border: `0.5px solid ${DA.goldSoft}`,
                  borderRadius: 10,
                }}
                data-testid={`onsite-note-${f.key}`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={DA.goldDeep} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2, flexShrink: 0 }} aria-hidden>
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <p style={{
                  fontFamily: DA.sans, fontSize: 11.5, color: DA.greenDeep,
                  lineHeight: 1.5, letterSpacing: '-0.005em',
                }}>
                  {f.onSiteNote}
                </p>
              </div>
            </div>
          );
        })()}

        {/* ── SUR DEVIS BLOCK (hybride) ── */}
        {(() => {
          const f = FORMATS.find((x) => x.key === format);
          if (!f || f.priceMode !== 'quote') return null;
          return (
            <div className="mt-4 pt-4" style={{ borderTop: `0.5px solid ${DA.goldSoft}` }}>
              <p style={{
                fontFamily: DA.sans, fontSize: 12.5, color: DA.inkMuted,
                lineHeight: 1.55,
              }}>
                Ce format est <strong style={{ color: DA.greenDeep }}>100 % sur-mesure</strong>. Je vous prépare un devis nominatif
                sous 24h ouvrées après un premier échange pour comprendre votre restaurant.
              </p>
            </div>
          );
        })()}

        {/* ── CHANTIERS (distanciel + audit48 — NOT hybride which is all-included) ── */}
        {(() => {
          const f = FORMATS.find((x) => x.key === format);
          if (!f || f.priceMode === 'quote' || f.priceMode === 'flat_all_included') return null;
          const isCustomMode = bundleKey === 'custom';
          const pickBundle = (b) => {
            setBundleKey(b.key);
            setSelected(b.chantiers.slice());
            if (setFullPack) setFullPack(false);
          };
          const switchToCustom = () => {
            setBundleKey('custom');
            // Do not clear selection — user may have preselected chantiers via "Ajouter au menu"
          };
          return (
            <div className="mt-4 pt-4" style={{ borderTop: `0.5px solid ${DA.goldSoft}` }}>
              <div className="mb-4">
                <p style={{
                  fontFamily: DA.mono, fontSize: 9.5, letterSpacing: '0.24em',
                  textTransform: 'uppercase', color: DA.goldDeep, fontWeight: 800,
                }}>
                  ② Boostez votre accompagnement
                </p>
                <p style={{
                  fontFamily: DA.serif, fontSize: 20, fontWeight: 900,
                  color: DA.greenDeep, letterSpacing: '-0.02em', lineHeight: 1.15,
                  marginTop: 6,
                }}>
                  Choisissez votre pack de chantiers.
                </p>
                <p style={{
                  fontFamily: DA.sans, fontSize: 12.5, color: DA.inkMuted,
                  lineHeight: 1.5, marginTop: 6,
                }}>
                  3 packs recommandés — je sais ce qu&apos;il vous faut, vous n&apos;avez pas à vous demander.
                </p>
              </div>

              {/* ── 3 CARTES PACKS ── */}
              <style>{`
                @keyframes bundleExpand {
                  from { opacity: 0; transform: translateY(-4px); max-height: 0; }
                  to   { opacity: 1; transform: translateY(0);    max-height: 800px; }
                }
              `}</style>
              <div className="flex flex-col gap-2.5 mb-3" data-testid="bundles-list">
                {BUNDLES.map((b) => {
                  const active = bundleKey === b.key;
                  const alaCarte = bundleAlaCartePrice(b);
                  const savings = Math.max(0, alaCarte - b.price);
                  return (
                    <button
                      key={b.key}
                      type="button"
                      onClick={() => pickBundle(b)}
                      data-testid={`bundle-${b.key}`}
                      aria-pressed={active}
                      className="w-full text-left transition-all active:scale-[0.995]"
                      style={{
                        background: active ? DA.paper : DA.ivoryLight,
                        border: `${active ? 1.5 : 0.5}px solid ${active ? DA.goldMain : DA.goldSoft}`,
                        borderRadius: 14,
                        padding: '14px 14px 14px 14px',
                        boxShadow: active
                          ? '0 12px 26px -12px rgba(11,43,36,0.30), 0 0 0 2px rgba(216,148,64,0.15)'
                          : '0 4px 10px -6px rgba(11,43,36,0.10)',
                        cursor: 'pointer',
                        position: 'relative',
                      }}
                    >
                      {b.badge && (
                        <span style={{
                          position: 'absolute', top: -8, right: 14,
                          fontFamily: DA.mono, fontSize: 8.5, fontWeight: 800,
                          letterSpacing: '0.16em', textTransform: 'uppercase',
                          color: '#FFF',
                          background: `linear-gradient(135deg, ${DA.goldMain} 0%, ${DA.goldDeep} 100%)`,
                          padding: '3px 9px', borderRadius: 999,
                          boxShadow: '0 4px 10px -3px rgba(216,148,64,0.55)',
                        }}>
                          {b.badge}
                        </span>
                      )}
                      <div className="flex items-start gap-3">
                        {/* Radio */}
                        <span
                          className="inline-flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{
                            width: 20, height: 20, borderRadius: 999,
                            border: `2px solid ${active ? DA.goldMain : DA.goldSoft}`,
                            background: active ? DA.goldMain : 'transparent',
                            color: '#FFF',
                          }}
                        >
                          {active && <Check size={12} strokeWidth={3.4} />}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-3">
                            <span style={{
                              fontFamily: DA.serif, fontSize: 17, fontWeight: 900,
                              color: DA.greenDeep, letterSpacing: '-0.02em',
                            }}>
                              {b.label}
                            </span>
                            <span style={{
                              fontFamily: DA.mono, fontSize: 13, fontWeight: 800,
                              color: DA.goldDeep, whiteSpace: 'nowrap',
                              fontVariantNumeric: 'tabular-nums',
                            }}>
                              +{fmt(b.price)} €
                            </span>
                          </div>
                          <p style={{
                            fontFamily: DA.sans, fontSize: 12, color: DA.inkMuted,
                            lineHeight: 1.4, marginTop: 3,
                          }}>
                            {b.subtitle}
                          </p>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span style={{
                              fontFamily: DA.mono, fontSize: 9, letterSpacing: '0.14em',
                              textTransform: 'uppercase', color: DA.greenDeep, fontWeight: 700,
                              background: 'rgba(216,148,64,0.10)',
                              padding: '2px 7px', borderRadius: 999,
                            }}>
                              {b.chantiers.length} chantier{b.chantiers.length > 1 ? 's' : ''}
                            </span>
                            {savings > 0 && (
                              <span style={{
                                fontFamily: DA.mono, fontSize: 9, letterSpacing: '0.14em',
                                textTransform: 'uppercase', color: DA.goldDeep, fontWeight: 800,
                              }}>
                                Économie {fmt(savings)} € HT
                              </span>
                            )}
                          </div>

                          {/* Guide-me hint : montre au persona 40-60 qu'il peut cliquer pour voir le détail */}
                          {!active && (
                            <p style={{
                              fontFamily: DA.mono, fontSize: 9, letterSpacing: '0.18em',
                              textTransform: 'uppercase', color: DA.inkFaint, fontWeight: 700,
                              marginTop: 8, display: 'flex', alignItems: 'center', gap: 4,
                            }}>
                              <ChevronDown size={11} strokeWidth={2.4} />
                              Tapez pour voir ce qui est inclus
                            </p>
                          )}

                          {/* ── Contenu du pack révélé quand actif ── */}
                          {active && (
                            <div
                              className="mt-3 pt-3"
                              style={{
                                borderTop: `0.5px dashed ${DA.goldSoft}`,
                                animation: 'bundleExpand 380ms cubic-bezier(0.32,0.72,0,1) both',
                              }}
                              data-testid={`bundle-${b.key}-content`}
                            >
                              <p style={{
                                fontFamily: DA.mono, fontSize: 8.5, letterSpacing: '0.24em',
                                textTransform: 'uppercase', color: DA.goldDeep, fontWeight: 800,
                                marginBottom: 8,
                              }}>
                                Ce pack inclut
                              </p>
                              <ul className="flex flex-col gap-2.5">
                                {b.chantiers.map((n) => {
                                  const c = CHANTIERS.find((x) => x.n === n);
                                  if (!c) return null;
                                  return (
                                    <li
                                      key={n}
                                      className="flex items-start gap-2"
                                      style={{
                                        fontFamily: DA.sans, fontSize: 12,
                                        color: DA.greenDeep, letterSpacing: '-0.005em',
                                        lineHeight: 1.35,
                                      }}
                                    >
                                      <span
                                        className="inline-flex items-center justify-center flex-shrink-0"
                                        style={{
                                          width: 14, height: 14, borderRadius: 999,
                                          background: DA.goldMain, color: '#FFF',
                                          marginTop: 3,
                                        }}
                                      >
                                        <Check size={8} strokeWidth={3.4} />
                                      </span>
                                      <span style={{ flex: 1 }}>
                                        <span style={{ display: 'block', fontWeight: 700 }}>
                                          <span style={{
                                            color: DA.goldDeep, fontFamily: DA.mono,
                                            fontSize: 9, fontWeight: 700, marginRight: 5,
                                          }}>
                                            {c.n}
                                          </span>
                                          {c.title}
                                        </span>
                                        {c.desc && (
                                          <span style={{
                                            display: 'block',
                                            marginTop: 3,
                                            fontSize: 10.5,
                                            fontStyle: 'italic',
                                            color: DA.inkBody,
                                            lineHeight: 1.4,
                                            letterSpacing: 0,
                                          }}>
                                            {c.desc}
                                          </span>
                                        )}
                                      </span>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* ── COMPARATEUR SIGNATURE vs HYBRIDE — visible only when Signature bundle selected in distanciel ── */}
              {bundleKey === 'signature' && format === 'distanciel' && (
                <div
                  className="mt-4 p-4"
                  data-testid="hybride-comparator"
                  style={{
                    background: `linear-gradient(135deg, ${DA.paper} 0%, ${DA.goldSofter} 100%)`,
                    border: `1px solid ${DA.goldMain}`,
                    borderRadius: 14,
                    boxShadow: '0 8px 20px -10px rgba(216,148,64,0.35)',
                  }}
                >
                  <p style={{
                    fontFamily: DA.mono, fontSize: 9, letterSpacing: '0.24em',
                    textTransform: 'uppercase', color: DA.goldDeep, fontWeight: 800,
                    marginBottom: 6,
                  }}>
                    ⚡ À prix équivalent · une alternative à considérer
                  </p>
                  <p style={{
                    fontFamily: DA.serif, fontSize: 16, fontWeight: 900,
                    color: DA.greenDeep, lineHeight: 1.25, marginBottom: 8,
                    letterSpacing: '-0.01em',
                  }}>
                    Vous avez choisi Signature en distanciel ({fmt(4990 + 19990)} € HT).<br/>
                    <span style={{ color: DA.goldDeep }}>Pour presque le même prix ({fmt(24990)} € HT)</span>, je viens 15 jours chez vous.
                  </p>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 10,
                    marginTop: 12,
                  }}>
                    <div style={{
                      background: DA.ivoryLight,
                      border: `0.5px solid ${DA.goldSoft}`,
                      borderRadius: 10,
                      padding: '10px 12px',
                    }}>
                      <p style={{ fontFamily: DA.mono, fontSize: 8.5, letterSpacing: '0.20em', color: DA.inkMuted, textTransform: 'uppercase', fontWeight: 700 }}>Votre choix actuel</p>
                      <p style={{ fontFamily: DA.serif, fontSize: 14, fontWeight: 900, color: DA.greenDeep, marginTop: 2 }}>Distanciel + Signature</p>
                      <p style={{ fontFamily: DA.sans, fontSize: 18, fontWeight: 900, color: DA.greenDeep, marginTop: 4 }}>{fmt(4990 + 19990)} € <span style={{ fontSize: 11, fontWeight: 700, color: DA.inkMuted }}>HT</span></p>
                      <ul style={{ margin: '8px 0 0', padding: 0, listStyle: 'none', fontSize: 11, color: DA.inkBody, lineHeight: 1.5 }}>
                        <li>✓ 12 chantiers stratégiques</li>
                        <li>✓ 90j coaching visio + WhatsApp</li>
                        <li style={{ color: DA.inkFaint }}>✗ Aucune présence terrain</li>
                      </ul>
                    </div>
                    <div style={{
                      background: DA.greenDeep,
                      border: `1px solid ${DA.goldMain}`,
                      borderRadius: 10,
                      padding: '10px 12px',
                      color: '#FFFFFF',
                    }}>
                      <p style={{ fontFamily: DA.mono, fontSize: 8.5, letterSpacing: '0.20em', color: DA.goldMain, textTransform: 'uppercase', fontWeight: 700 }}>Recommandation Sabril</p>
                      <p style={{ fontFamily: DA.serif, fontSize: 14, fontWeight: 900, color: '#FFFFFF', marginTop: 2 }}>Format Hybride</p>
                      <p style={{ fontFamily: DA.sans, fontSize: 18, fontWeight: 900, color: '#FFFFFF', marginTop: 4 }}>{fmt(24990)} € <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>HT</span></p>
                      <ul style={{ margin: '8px 0 0', padding: 0, listStyle: 'none', fontSize: 11, color: 'rgba(255,255,255,0.92)', lineHeight: 1.5 }}>
                        <li>✓ 12 chantiers stratégiques</li>
                        <li>✓ 75j coaching visio + WhatsApp</li>
                        <li style={{ color: DA.goldMain, fontWeight: 700 }}>✓ 15 JOURS avec moi sur site</li>
                        <li style={{ color: DA.goldMain, fontWeight: 700 }}>✓ Vol + hôtel offerts</li>
                      </ul>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setFormat('hybride');
                      setBundleKey(null);
                      setSelected([]);
                      if (setFullPack) setFullPack(false);
                    }}
                    data-testid="switch-to-hybride-btn"
                    className="w-full mt-3 py-3 transition-all active:scale-[0.99]"
                    style={{
                      background: DA.goldMain,
                      color: DA.greenDeep,
                      border: 'none',
                      borderRadius: 10,
                      fontFamily: DA.sans,
                      fontSize: 12.5,
                      fontWeight: 800,
                      letterSpacing: '0.03em',
                      cursor: 'pointer',
                      boxShadow: '0 6px 14px -6px rgba(216,148,64,0.55)',
                    }}
                  >
                    ⚡ Passer au Format Hybride (+10&nbsp;€) — 15j terrain, vol &amp; hôtel offerts
                  </button>
                  <p style={{
                    fontFamily: DA.sans, fontSize: 10, color: DA.inkMuted, fontStyle: 'italic',
                    textAlign: 'center', marginTop: 8, lineHeight: 1.4,
                  }}>
                    Tarif tout compris. Vol A/R + hébergement 3★ pris en charge par Sabril.
                  </p>
                </div>
              )}

              {/* ── ESCAPE HATCH · Sur-mesure ── */}
              <button
                type="button"
                onClick={switchToCustom}
                aria-pressed={isCustomMode}
                data-testid="bundle-custom"
                className="w-full flex items-center justify-center gap-2 py-3 mb-3 transition-all"
                style={{
                  background: isCustomMode ? DA.paper : 'transparent',
                  border: `${isCustomMode ? 1 : 0.5}px dashed ${isCustomMode ? DA.goldMain : DA.goldSoft}`,
                  borderRadius: 12,
                  fontFamily: DA.sans, fontSize: 12, fontWeight: 700,
                  color: isCustomMode ? DA.greenDeep : DA.inkMuted,
                  cursor: 'pointer',
                  letterSpacing: '-0.005em',
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></svg>
                {isCustomMode ? 'Je personnalise mes options' : 'Ou je préfère cocher moi-même mes options'}
              </button>

              {/* ── Chantiers individuels · custom mode ONLY ── */}
              {isCustomMode && (
                <div className="pt-1">
                  <div className="flex items-center justify-end mb-3">
                    <button
                      type="button"
                      onClick={() => {
                        const allOpen = CHANTIERS.every((cc) => expandedDesc && expandedDesc.has(cc.n));
                        toggleAllDesc && toggleAllDesc(!allOpen);
                      }}
                      className="inline-flex items-center gap-1 px-2.5 py-1 transition-all"
                      style={{
                        background: DA.goldSofter,
                        border: `0.5px solid ${DA.goldSoft}`,
                        borderRadius: 999,
                        fontFamily: DA.mono, fontSize: 9.5, fontWeight: 800,
                        letterSpacing: '0.16em', textTransform: 'uppercase',
                        color: DA.goldDeep,
                      }}
                      data-testid="toggle-all-desc"
                    >
                      {(() => {
                        const allOpen = CHANTIERS.every((cc) => expandedDesc && expandedDesc.has(cc.n));
                        return allOpen
                          ? (<>Tout masquer <ChevronDown size={11} strokeWidth={2.2} style={{ transform: 'rotate(180deg)' }} /></>)
                          : (<>Voir tous les détails <ChevronDown size={11} strokeWidth={2.2} /></>);
                      })()}
                    </button>
                  </div>

              {/* Chantier grid */}
              <div className="flex flex-col gap-1.5" style={{ opacity: fullPack ? 0.45 : 1, pointerEvents: fullPack ? 'none' : 'auto' }}>
                {CHANTIERS.map((c) => {
                  const isChecked = selected.includes(c.n) || fullPack;
                  const isOpen = expandedDesc && expandedDesc.has(c.n);
                  const locked = !fullPack && isBundleLocked(c.n);
                  return (
                    <div
                      key={c.n}
                      style={{
                        background: isChecked ? DA.ivoryLight : 'transparent',
                        borderRadius: 10,
                        border: `${isChecked ? 1 : 0.5}px solid ${isChecked ? DA.goldMain : DA.goldSoft}`,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleChantier(c.n)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault(); toggleChantier(c.n);
                          }
                        }}
                        className="text-left"
                        style={{
                          padding: '10px 10px 10px 12px',
                          display: 'flex', alignItems: 'center', gap: 10,
                          cursor: locked ? 'not-allowed' : 'pointer',
                        }}
                        data-testid={`chantier-inline-${c.n}`}
                        aria-disabled={locked}
                      >
                        <span
                          className="inline-flex items-center justify-center flex-shrink-0"
                          style={{
                            width: 18, height: 18, borderRadius: 5,
                            border: `1.2px solid ${isChecked ? DA.goldMain : DA.goldSoft}`,
                            background: isChecked ? DA.goldMain : 'transparent',
                            color: '#FFF',
                          }}
                        >
                          {isChecked && <Check size={11} strokeWidth={3.2} />}
                        </span>
                        <span style={{
                          fontFamily: DA.mono, fontSize: 9, fontWeight: 700,
                          letterSpacing: '0.10em', color: DA.goldDeep,
                          flexShrink: 0, opacity: 0.85,
                        }}>
                          {c.n}
                        </span>
                        <span style={{
                          fontFamily: DA.sans, fontSize: 13, fontWeight: 600,
                          color: DA.greenDeep, letterSpacing: '-0.01em',
                          lineHeight: 1.25, flex: 1, minWidth: 0,
                        }}>
                          {c.title}
                          {locked && (
                            <span
                              className="ml-1.5 inline-flex items-center gap-1"
                              data-testid={`chantier-locked-${c.n}`}
                              style={{
                                fontFamily: DA.mono, fontSize: 8.5, fontWeight: 700,
                                letterSpacing: '0.12em', textTransform: 'uppercase',
                                color: DA.goldDeep,
                                background: 'rgba(216,148,64,0.14)',
                                padding: '2px 6px 2px 5px', borderRadius: 999,
                                verticalAlign: '2px', whiteSpace: 'nowrap',
                              }}
                            >
                              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                              Inclus avec 11
                            </span>
                          )}
                        </span>
                        <span style={{
                          fontFamily: DA.mono, fontSize: 9.5, fontWeight: 800,
                          letterSpacing: '0.06em', color: DA.goldMain, flexShrink: 0,
                          whiteSpace: 'nowrap',
                        }}>
                          +{fmt(priceOf(c))} €
                        </span>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleDesc && toggleDesc(c.n); }}
                          aria-label={isOpen ? 'Masquer le détail' : 'Voir le détail'}
                          aria-expanded={isOpen}
                          className="inline-flex items-center justify-center flex-shrink-0 transition-transform"
                          style={{
                            width: 26, height: 26, borderRadius: 8,
                            background: isOpen ? DA.goldSofter : 'transparent',
                            color: DA.goldDeep,
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            marginLeft: 4,
                          }}
                          data-testid={`chantier-toggle-${c.n}`}
                        >
                          <ChevronDown size={16} strokeWidth={2.2} />
                        </button>
                      </div>
                      {isOpen && (
                        <div
                          style={{
                            padding: '2px 14px 12px 40px',
                            borderTop: `0.5px solid ${DA.goldSoft}`,
                            background: 'rgba(216,148,64,0.05)',
                          }}
                          data-testid={`chantier-desc-${c.n}`}
                        >
                          <p style={{
                            fontFamily: DA.sans, fontSize: 12,
                            color: DA.inkMuted, lineHeight: 1.55,
                            letterSpacing: '-0.005em', paddingTop: 8,
                          }}>
                            {c.desc}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
                </div>
              )}

              {/* Resto 360 notice — applies in bundle mode too (Complet & Signature include ch13) */}
              {(fullPack || selected.includes('13')) && (
                <div
                  className="mt-3 flex items-start gap-2 px-3 py-2"
                  style={{
                    background: DA.ivoryDeep,
                    border: `0.5px solid ${DA.goldMain}`,
                    borderRadius: 10,
                  }}
                  data-testid="resto360-notice"
                >
                  <span style={{ fontFamily: DA.mono, fontSize: 11, fontWeight: 800, color: DA.goldDeep, marginTop: 1 }}>⚠</span>
                  <p style={{ fontFamily: DA.sans, fontSize: 11, color: DA.greenDeep, lineHeight: 1.45 }}>
                    <strong>Chantier N°13 · Resto 360</strong> — Le paramétrage est inclus, mais
                    l&apos;<strong>abonnement Resto 360 (349 €/mois)</strong> reste à votre charge.
                  </p>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── LIVE TOTAL + CTA ── */}
        {/* ── RÉCAPITULATIF + 2 CTAs FINAUX ── */}
        {(() => {
          const f = FORMATS.find((x) => x.key === format);
          if (!f) return null;
          const isFlat = f.priceMode === 'flat_all_included';
          const currentBase = f.basePrice || 0;
          // Bundle selected? → show as SINGLE line "Pack X · +price"
          const selectedBundle = bundleKey && bundleKey !== 'custom' ? findBundle(bundleKey) : null;
          const chantierLines = (isFlat || selectedBundle)
            ? []
            : selected.map((n) => {
                const c = CHANTIERS.find((x) => x.n === n);
                return c ? { n, title: c.title, price: priceOf(c) } : null;
              }).filter(Boolean);
          const total = isFlat
            ? currentBase
            : (selectedBundle
                ? currentBase + selectedBundle.price
                : (fullPack ? FULL_PACK_PRICE : currentBase + selectedSum));
          const goFinalize = (target) => {
            try {
              sessionStorage.setItem('sabril_config_prefill', JSON.stringify({
                format: f.key,
                format_label: f.label,
                country: null,
                chantiers: (isFlat || fullPack) ? CHANTIERS.map((cc) => cc.n) : selected,
                full_pack: isFlat || fullPack,
                bundle_key: selectedBundle?.key || null,
                bundle_label: selectedBundle?.label || null,
                total_ht: total,
                base_price: currentBase || null,
                cta_target: target, // 'sign' | 'whatsapp'
                createdAt: Date.now(),
              }));
            } catch (_e) { /* ignore */ }
            if (target === 'whatsapp') {
              window.location.href = WHATSAPP_URL;
            } else {
              window.location.href = '/configurer/finaliser';
            }
          };
          return (
            <div className="mt-5" data-testid="config-recap">
              {/* Récap section header */}
              <p style={{
                fontFamily: DA.mono, fontSize: 9.5, letterSpacing: '0.24em',
                textTransform: 'uppercase', color: DA.goldDeep, fontWeight: 800,
                marginBottom: 8,
              }}>
                ③ Votre accompagnement
              </p>

              {/* Reçu-like card */}
              <div
                style={{
                  background: DA.ivoryLight,
                  borderRadius: 14,
                  border: `1px solid ${DA.goldMain}`,
                  padding: '14px 14px 16px',
                  boxShadow: '0 10px 24px -14px rgba(11,43,36,0.20)',
                }}
              >
                {/* Base line */}
                <div className="flex items-baseline justify-between gap-3" style={{
                  paddingBottom: 10, borderBottom: `0.5px solid ${DA.goldSoft}`,
                }}>
                  <span style={{
                    fontFamily: DA.sans, fontSize: 13, fontWeight: 700,
                    color: DA.greenDeep, letterSpacing: '-0.01em',
                  }}>
                    {f.label}
                  </span>
                  <span style={{
                    fontFamily: DA.mono, fontSize: 12, fontWeight: 800,
                    color: DA.goldDeep, whiteSpace: 'nowrap',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    {isFlat ? `${fmt(f.basePrice)} €` : `${fmt(currentBase)} €`}
                  </span>
                </div>
                {/* Option lines */}
                {selectedBundle && (
                  <div className="flex items-baseline justify-between gap-3 mt-2.5">
                    <span style={{
                      fontFamily: DA.sans, fontSize: 12.5, color: DA.greenDeep, letterSpacing: '-0.005em', fontWeight: 700,
                    }}>
                      <span style={{
                        color: DA.goldDeep, fontFamily: DA.mono, fontSize: 9,
                        letterSpacing: '0.14em', textTransform: 'uppercase',
                        fontWeight: 800, marginRight: 6,
                      }}>Pack</span>
                      {selectedBundle.label} · {selectedBundle.chantiers.length} chantiers
                    </span>
                    <span style={{
                      fontFamily: DA.mono, fontSize: 11.5, fontWeight: 800,
                      color: DA.goldMain, whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      +{fmt(selectedBundle.price)} €
                    </span>
                  </div>
                )}
                {chantierLines.length > 0 && chantierLines.map((l) => (
                  <div key={l.n} className="flex items-baseline justify-between gap-3 mt-2">
                    <span style={{
                      fontFamily: DA.sans, fontSize: 12, color: DA.greenDeep, letterSpacing: '-0.005em',
                    }}>
                      <span style={{ color: DA.goldDeep, fontFamily: DA.mono, fontSize: 9.5, fontWeight: 700, marginRight: 6 }}>{l.n}</span>
                      {l.title}
                    </span>
                    <span style={{
                      fontFamily: DA.mono, fontSize: 11, fontWeight: 800,
                      color: DA.goldMain, whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      +{fmt(l.price)} €
                    </span>
                  </div>
                ))}
                {isFlat && (
                  <p style={{
                    fontFamily: DA.sans, fontSize: 11.5, color: DA.inkMuted,
                    fontStyle: 'italic', marginTop: 8, lineHeight: 1.4,
                  }}>
                    Les 12 chantiers stratégiques sont inclus dans ce format.
                  </p>
                )}
                {!isFlat && !selectedBundle && chantierLines.length === 0 && (
                  <p style={{
                    fontFamily: DA.sans, fontSize: 11.5, color: DA.inkMuted,
                    fontStyle: 'italic', marginTop: 8, lineHeight: 1.4,
                  }}>
                    Aucun pack sélectionné — cochez un pack ci-dessus pour ajouter des chantiers.
                  </p>
                )}
                {/* Total */}
                <div className="flex items-baseline justify-between mt-3 pt-3" style={{
                  borderTop: `1px solid ${DA.goldMain}`,
                }}>
                  <span style={{
                    fontFamily: DA.mono, fontSize: 10, letterSpacing: '0.2em',
                    textTransform: 'uppercase', color: DA.greenDeep, fontWeight: 800,
                  }}>
                    Total HT
                  </span>
                  <AnimatedPrice
                    value={total}
                    style={{
                      fontFamily: DA.serif, fontSize: 22, fontWeight: 900,
                      color: DA.greenDeep, letterSpacing: '-0.02em',
                    }}
                    data-testid="config-total"
                  />
                </div>
              </div>

              {/* ── 2 CTAs FINAUX — WAITLIST MODE (accès sur choix personnel) ── */}
              <div className="mt-5 flex flex-col gap-2.5">
                {/* Étiquette "Complet" — scarcity Hermès */}
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1"
                    style={{
                      background: 'rgba(216,148,64,0.14)',
                      border: `0.5px solid ${DA.goldMain}`,
                      borderRadius: 999,
                      fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
                      letterSpacing: '0.28em', textTransform: 'uppercase',
                      color: DA.goldDeep,
                    }}
                    data-testid="waitlist-badge"
                  >
                    <span style={{ width: 6, height: 6, borderRadius: 999, background: DA.goldDeep }} />
                    Complet ce trimestre
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => goFinalize('sign')}
                  className="w-full flex flex-col items-center justify-center gap-0.5 px-4 py-3.5 transition-all active:scale-[0.99]"
                  style={{
                    background: DA.greenDeep,
                    borderRadius: 14,
                    color: DA.ivory,
                    border: `0.5px solid ${DA.goldMain}`,
                    boxShadow: '0 14px 28px -10px rgba(11,43,36,0.45), inset 0 1px 0 rgba(216,148,64,0.30)',
                    fontFamily: DA.sans,
                    letterSpacing: '-0.01em',
                  }}
                  data-testid="cta-sign"
                >
                  <span className="inline-flex items-center gap-2" style={{ fontSize: 14, fontWeight: 800 }}>
                    <FileSignature size={16} strokeWidth={2.2} style={{ color: DA.goldMain }} />
                    Rejoindre la liste d&apos;attente
                  </span>
                  <span style={{
                    fontFamily: DA.mono, fontSize: 8.5, fontWeight: 700,
                    letterSpacing: '0.20em', textTransform: 'uppercase',
                    color: 'rgba(216,148,64,0.85)', marginTop: 3,
                    textAlign: 'center',
                  }}>
                    Sabril vous recontacte dès qu&apos;une place se libère
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => goFinalize('whatsapp')}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3.5 transition-all active:scale-[0.98]"
                  style={{
                    background: 'transparent',
                    borderRadius: 14,
                    color: DA.greenDeep,
                    border: `1px solid ${DA.goldMain}`,
                    fontFamily: DA.sans, fontSize: 13.5, fontWeight: 700,
                    letterSpacing: '-0.005em',
                  }}
                  data-testid="cta-whatsapp"
                >
                  <MessageCircle size={16} strokeWidth={2.2} style={{ color: DA.goldDeep }} />
                  Discuter avec Sabril d&apos;abord
                </button>
              </div>
            </div>
          );
        })()}
      </div>

      {onNext && (
        <div className="mt-6">
          <PrimaryButton onClick={onNext} testid="format-next">
            Continuer <ChevronRight size={16} strokeWidth={2.2} />
          </PrimaryButton>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// STEP 2 — CHANTIERS
// ─────────────────────────────────────────────────────────────────────
function StepChantiers({ selected, toggle, fullPack, setFullPack, format, country, total, savings, onBack, onNext }) {
  const isDistanciel = format.key === 'distanciel';
  const shouldSuggestPack = isDistanciel && selected.length >= 8 && !fullPack;

  return (
    <div className="animate-fadein" data-testid="step-chantiers">
      <SectionHead
        overline="② Chantiers"
        title="Quels chantiers souhaitez-vous traiter ?"
        subtitle="Cochez uniquement ce dont votre restaurant a besoin. Le prix se met à jour en temps réel."
      />

      {/* ── Full-pack toggle ── */}
      {isDistanciel && (
        <div className="mt-8">
          <FullPackCard
            active={fullPack}
            onToggle={() => setFullPack((v) => !v)}
            suggested={shouldSuggestPack}
          />
        </div>
      )}

      {/* ── Smart suggestion banner ── */}
      {shouldSuggestPack && (
        <div
          className="mt-4 flex items-center gap-3 px-4 py-3"
          style={{
            background: DA.goldSofter,
            border: `0.5px dashed ${DA.goldMain}`,
            borderRadius: 12,
          }}
          data-testid="pack-suggestion"
        >
          <Sparkles size={16} strokeWidth={2} style={{ color: DA.goldDeep }} />
          <p style={{ fontFamily: DA.sans, fontSize: 12, color: DA.greenDeep, lineHeight: 1.4 }}>
            Vous avez sélectionné <strong>{selected.length} chantiers</strong>. Le pack complet à {fmt(FULL_PACK_PRICE)} € HT vous fait économiser <strong>{fmt(Math.max(0, savings || 0))} € HT</strong>.
          </p>
        </div>
      )}

      {/* ── Resto 360 subscription notice ── */}
      {(fullPack || selected.includes('13')) && (
        <div
          className="mt-4 flex items-start gap-3 px-4 py-3"
          style={{
            background: DA.ivoryDeep,
            border: `0.5px solid ${DA.goldMain}`,
            borderRadius: 12,
          }}
          data-testid="resto360-notice"
        >
          <span style={{
            fontFamily: DA.mono, fontSize: 12, fontWeight: 800,
            color: DA.goldDeep, marginTop: 2,
          }}>⚠</span>
          <p style={{ fontFamily: DA.sans, fontSize: 12, color: DA.greenDeep, lineHeight: 1.5 }}>
            <strong>Chantier N°13 · Intégration Resto 360</strong> — Le paramétrage est inclus,
            mais l&apos;abonnement à l&apos;application <strong>Resto 360 (349 €/mois)</strong> reste
            à votre charge, facturé directement par l&apos;éditeur.
          </p>
        </div>
      )}

      {/* ── Chantier grid ── */}
      <div className="grid sm:grid-cols-2 gap-2.5 mt-6" style={{ opacity: fullPack ? 0.45 : 1, pointerEvents: fullPack ? 'none' : 'auto' }}>
        {CHANTIERS.map((c) => {
          const active = selected.includes(c.n) || fullPack;
          return (
            <button
              key={c.n}
              onClick={() => toggle(c.n)}
              className="text-left transition-all"
              style={{
                background: active ? DA.paper : DA.ivoryLight,
                borderRadius: 14,
                padding: '12px 14px',
                border: `${active ? 1 : 0.5}px solid ${active ? DA.goldMain : DA.goldSoft}`,
                cursor: 'pointer',
              }}
              data-testid={`chantier-${c.n}`}
            >
              <div className="flex items-start gap-2.5">
                {/* Checkbox */}
                <span
                  className="inline-flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{
                    width: 18, height: 18, borderRadius: 5,
                    border: `1.2px solid ${active ? DA.goldMain : DA.goldSoft}`,
                    background: active ? DA.goldMain : 'transparent',
                    color: '#FFF',
                  }}
                >
                  {active && <Check size={11} strokeWidth={3.2} />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span style={{
                      fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
                      letterSpacing: '0.16em', color: DA.goldDeep,
                    }}>
                      N°{c.n}
                    </span>
                    <h4 style={{
                      fontFamily: DA.serif, fontSize: 14.5, fontWeight: 900,
                      color: DA.greenDeep, letterSpacing: '-0.015em', lineHeight: 1.15, flex: 1,
                    }}>
                      {c.title}
                    </h4>
                  </div>
                  <p style={{
                    fontFamily: DA.sans, fontSize: 11.5, color: DA.inkMuted,
                    lineHeight: 1.45, marginTop: 4,
                  }}>
                    {c.desc}
                  </p>
                  <p style={{
                    fontFamily: DA.mono, fontSize: 9.5, fontWeight: 800,
                    letterSpacing: '0.14em', color: DA.goldMain, marginTop: 6,
                  }}>
                    +{fmt(priceOf(c))} € HT
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Sticky total bar (only in wizard mode) ── */}
      {onNext && (
        <StickyTotal
          format={format}
          country={country}
          selected={selected}
          fullPack={fullPack}
          total={total}
          onBack={onBack}
          onNext={onNext}
          nextLabel="Récapitulatif"
        />
      )}
    </div>
  );
}

// ─── Full pack card ──
function FullPackCard({ active, onToggle, suggested }) {
  return (
    <button
      onClick={onToggle}
      className="relative w-full text-left overflow-hidden transition-all"
      style={{
        background: active ? DA.greenDeep : DA.paper,
        borderRadius: 20,
        padding: '20px 22px',
        border: `${active || suggested ? 1.5 : 0.5}px solid ${active ? DA.goldMain : suggested ? DA.goldMain : DA.goldSoft}`,
        boxShadow: active ? '0 20px 40px -18px rgba(11,43,36,0.30)' : 'none',
        cursor: 'pointer',
      }}
      data-testid="full-pack-toggle"
    >
      {/* Ribbon */}
      <div
        className="absolute top-3 right-3 flex items-center gap-1"
        style={{
          background: DA.goldMain,
          color: DA.paper,
          padding: '4px 10px',
          borderRadius: 20,
          fontFamily: DA.mono, fontSize: 9, fontWeight: 800,
          letterSpacing: '0.16em', textTransform: 'uppercase',
        }}
      >
        <Sparkles size={10} strokeWidth={2.5} />
        Le plus choisi
      </div>

      <div className="flex items-start gap-3.5 pr-24">
        <span
          className="inline-flex items-center justify-center flex-shrink-0 mt-1"
          style={{
            width: 22, height: 22, borderRadius: '50%',
            border: `1.5px solid ${active ? DA.goldMain : DA.goldSoft}`,
            background: active ? DA.goldMain : 'transparent',
            color: DA.paper,
          }}
        >
          {active && <Check size={12} strokeWidth={3} />}
        </span>
        <div className="flex-1">
          <p style={{
            fontFamily: DA.mono, fontSize: 9.5, letterSpacing: '0.24em',
            textTransform: 'uppercase', fontWeight: 800,
            color: active ? DA.goldMain : DA.goldDeep, marginBottom: 6,
          }}>
            Pack complet 360°
          </p>
          <h3 style={{
            fontFamily: DA.serif, fontSize: 22, fontWeight: 900,
            color: active ? DA.ivory : DA.greenDeep,
            letterSpacing: '-0.024em', lineHeight: 1.1,
          }}>
            L&apos;accompagnement complet.<br />
            <span style={{ fontStyle: 'italic', color: active ? DA.goldMain : DA.goldDeep }}>
              {fmt(FULL_PACK_PRICE)} € HT
            </span>
          </h3>
          <ul className="mt-3 space-y-1.5">
            {[
              `Les ${CHANTIERS.length} chantiers inclus`,
              'Méthode complète Sabril',
              'Suivi personnalisé à distance',
              'Tous les documents et outils',
            ].map((li) => (
              <li key={li} className="flex items-start gap-1.5" style={{
                fontFamily: DA.sans, fontSize: 12,
                color: active ? 'rgba(247,238,223,0.85)' : DA.inkMuted,
              }}>
                <Check size={12} strokeWidth={2.4} style={{ marginTop: 3, color: active ? DA.goldMain : DA.goldDeep }} />
                {li}
              </li>
            ))}
          </ul>
          <p style={{
            fontFamily: DA.sans, fontSize: 10.5, fontStyle: 'italic', marginTop: 10,
            color: active ? 'rgba(216,148,64,0.85)' : DA.inkFaint,
          }}>
            Économisez ~8 930 € HT vs. les chantiers achetés séparément.
          </p>
        </div>
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// STEP 3 — RÉCAPITULATIF + FORMULAIRE
// ─────────────────────────────────────────────────────────────────────
function StepRecap({
  format, country, selected, fullPack, total, priceLabel, isQuote,
  contact, setContact, canSubmit, submitting, onBack, onSubmit,
}) {
  const set = (k) => (e) => setContact({ ...contact, [k]: e.target.value });
  const listedChantiers = fullPack ? CHANTIERS : CHANTIERS.filter((c) => selected.includes(c.n));

  return (
    <div className="animate-fadein" data-testid="step-recap">
      <SectionHead
        overline="② Devis & signature"
        title="Votre accompagnement."
        subtitle="Vérifiez, laissez vos coordonnées, validez le devis ou signez directement votre contrat."
      />

      {/* Récap card */}
      <article
        className="relative overflow-hidden mt-8"
        style={{
          background: DA.paper,
          borderRadius: 22,
          padding: '26px 22px',
          border: `0.5px solid ${DA.goldSoft}`,
          boxShadow: '0 18px 40px -18px rgba(11,43,36,0.15)',
        }}
        data-testid="recap-card"
      >
        <AwningCorner position="top-left" size={100} />
        <MimosaBranch size={110} position="bottom-right" opacity={0.85} />

        <div className="relative" style={{ zIndex: 2 }}>
          {/* Format */}
          <p style={{
            fontFamily: DA.mono, fontSize: 9.5, letterSpacing: '0.24em',
            textTransform: 'uppercase', color: DA.goldDeep, fontWeight: 800, marginBottom: 6,
          }}>
            Format retenu
          </p>
          <h3 style={{
            fontFamily: DA.serif, fontSize: 22, fontWeight: 900,
            color: DA.greenDeep, letterSpacing: '-0.02em', lineHeight: 1.1,
          }}>
            {format.label}
          </h3>
          {format.key === 'audit48' && (
            <p style={{ fontFamily: DA.sans, fontSize: 12, color: DA.inkMuted, marginTop: 4 }}>
              Destination : {COUNTRIES.find((c) => c.code === country)?.flag} {COUNTRIES.find((c) => c.code === country)?.label}
            </p>
          )}

          {/* Chantiers */}
          {listedChantiers.length > 0 && (
            <>
              <div className="my-4" style={{ height: 0.5, background: DA.goldSoft }} />
              <p style={{
                fontFamily: DA.mono, fontSize: 9.5, letterSpacing: '0.24em',
                textTransform: 'uppercase', color: DA.goldDeep, fontWeight: 800, marginBottom: 8,
              }}>
                {fullPack ? `Pack complet · ${CHANTIERS.length} chantiers` : `Chantiers sélectionnés · ${listedChantiers.length}`}
              </p>
              <ul className="space-y-1">
                {listedChantiers.map((c) => (
                  <li key={c.n} className="flex items-baseline gap-2" style={{
                    fontFamily: DA.sans, fontSize: 12.5, color: DA.greenDeep,
                  }}>
                    <Check size={11} strokeWidth={2.6} style={{ color: DA.goldMain, flexShrink: 0 }} />
                    <span style={{ fontFamily: DA.mono, fontSize: 9.5, fontWeight: 800, color: DA.goldDeep }}>
                      {c.n}
                    </span>
                    <span>{c.title}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Total */}
          <div className="my-4" style={{ height: 0.5, background: DA.goldSoft }} />
          <div className="flex items-baseline justify-between">
            <p style={{
              fontFamily: DA.mono, fontSize: 10, letterSpacing: '0.24em',
              textTransform: 'uppercase', color: DA.goldDeep, fontWeight: 800,
            }}>
              Total {isQuote ? '' : 'HT'}
            </p>
            <p style={{
              fontFamily: DA.serif, fontSize: 36, fontWeight: 900,
              color: DA.greenDeep, letterSpacing: '-0.03em',
            }}>
              {priceLabel}
            </p>
          </div>
          {isQuote && (
            <p style={{
              fontFamily: DA.sans, fontSize: 11.5, fontStyle: 'italic',
              color: DA.inkMuted, textAlign: 'right', marginTop: 2,
            }}>
              Format terrain — devis personnalisé sous 48h.
            </p>
          )}
        </div>
      </article>

      {/* Contact form */}
      <div className="mt-8" data-testid="config-contact-form">
        <p style={{
          fontFamily: DA.mono, fontSize: 10, letterSpacing: '0.28em',
          textTransform: 'uppercase', color: DA.goldDeep, fontWeight: 800, marginBottom: 14,
        }}>
          Vos coordonnées
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input label="Prénom" value={contact.firstName} onChange={set('firstName')} testid="input-firstname" />
          <Input label="Restaurant" value={contact.restaurant} onChange={set('restaurant')} testid="input-restaurant" />
          <Input label="Email" type="email" value={contact.email} onChange={set('email')} testid="input-email" />
          <Input label="WhatsApp / Téléphone" value={contact.phone} onChange={set('phone')} testid="input-phone" placeholder="+33 6 12 34 56 78" />
        </div>
        <Input label="Notes (optionnel)" value={contact.notes} onChange={set('notes')} testid="input-notes" multiline />
      </div>

      {/* CTAs */}
      <div className="mt-8 flex flex-col gap-3">
        <PrimaryButton
          onClick={() => onSubmit('quote')}
          disabled={!canSubmit || submitting}
          testid="submit-quote"
        >
          <MessageCircle size={16} strokeWidth={2.2} />
          {submitting ? 'Envoi…' : 'Demander mon devis'}
        </PrimaryButton>
        {!isQuote && (
          <SecondaryButton
            onClick={() => onSubmit('contract')}
            disabled={!canSubmit || submitting}
            testid="submit-contract"
          >
            <FileSignature size={16} strokeWidth={2} />
            Signer mon contrat directement
          </SecondaryButton>
        )}
      </div>

      {onBack && (
        <div className="mt-6">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-1.5"
            style={{
              fontFamily: DA.mono, fontSize: 10, letterSpacing: '0.24em',
              textTransform: 'uppercase', color: DA.inkMuted, fontWeight: 700,
            }}
            data-testid="recap-back"
          >
            <ChevronLeft size={12} strokeWidth={2.2} />
            Modifier ma sélection
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// STEP 4 — SUCCESS
// ─────────────────────────────────────────────────────────────────────
function StepSuccess({ onBack }) {
  return (
    <div className="text-center py-10 animate-fadein" data-testid="step-success">
      <div className="flex justify-center mb-6">
        <SabrilStamp size={72} />
      </div>
      <p style={{
        fontFamily: DA.mono, fontSize: 11, fontWeight: 800, letterSpacing: '0.32em',
        textTransform: 'uppercase', color: DA.goldMain, marginBottom: 14,
      }}>
        Demande reçue
      </p>
      <h2 style={{
        fontFamily: DA.serif, fontSize: 38, fontWeight: 900,
        color: DA.greenDeep, letterSpacing: '-0.028em', lineHeight: 1.05,
      }}>
        Merci.<br />Je vous réponds sous 24h.
      </h2>
      <p style={{
        fontFamily: DA.sans, fontSize: 14, color: DA.inkMuted,
        lineHeight: 1.55, maxWidth: 420, margin: '20px auto',
      }}>
        Vous recevez un email de confirmation. Je vous contacte personnellement sur WhatsApp pour valider votre plan et vous transmettre votre contrat.
      </p>
      <div className="flex justify-center mt-8">
        <SabrilSignature size={54} color={DA.greenDeep} />
      </div>
      <button
        onClick={onBack}
        className="mt-10 inline-flex items-center gap-1.5"
        style={{
          fontFamily: DA.mono, fontSize: 10, letterSpacing: '0.24em',
          textTransform: 'uppercase', color: DA.inkMuted, fontWeight: 700,
        }}
        data-testid="success-back-home"
      >
        <ChevronLeft size={12} strokeWidth={2.2} />
        Retour à l&apos;accueil
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// STICKY TOTAL BAR — mobile / desktop
// ─────────────────────────────────────────────────────────────────────
function StickyTotal({ format, country, selected, fullPack, total, onBack, onNext, nextLabel }) {
  const isQuote = format.priceMode === 'quote';
  return (
    <div
      className="sticky bottom-3 mt-8 mx-auto"
      style={{ zIndex: 30, maxWidth: 640 }}
      data-testid="sticky-total"
    >
      <div
        className="flex items-center gap-2.5 px-3 py-2.5"
        style={{
          background: DA.greenDeep,
          borderRadius: 16,
          border: `0.5px solid ${DA.goldMain}`,
          boxShadow: '0 20px 40px -14px rgba(11,43,36,0.45)',
        }}
      >
        {/* Back circle */}
        <button
          onClick={onBack}
          className="inline-flex items-center justify-center flex-shrink-0"
          style={{
            width: 34, height: 34, borderRadius: '50%',
            background: 'rgba(247,238,223,0.10)',
            color: DA.ivory, border: `0.5px solid rgba(216,148,64,0.4)`,
          }}
          data-testid="sticky-back"
          aria-label="Retour"
        >
          <ChevronLeft size={16} strokeWidth={2.4} />
        </button>

        {/* Compact price block */}
        <div className="flex-shrink-0" style={{ minWidth: 78 }}>
          <p style={{
            fontFamily: DA.mono, fontSize: 8, letterSpacing: '0.2em',
            textTransform: 'uppercase', color: DA.goldMain, fontWeight: 800,
            lineHeight: 1,
          }}>
            {fullPack ? 'Pack 360°' : `${selected.length} chantier${selected.length > 1 ? 's' : ''}`}
          </p>
          {isQuote ? (
            <p style={{
              fontFamily: DA.serif, fontSize: 18, fontWeight: 900,
              color: DA.ivory, letterSpacing: '-0.02em', lineHeight: 1.1, marginTop: 3,
              whiteSpace: 'nowrap',
            }}>Sur devis</p>
          ) : (
            <AnimatedPrice
              value={total}
              suffix=" € HT"
              style={{
                fontFamily: DA.serif, fontSize: 18, fontWeight: 900,
                color: DA.ivory, letterSpacing: '-0.02em', lineHeight: 1.1, marginTop: 3,
                whiteSpace: 'nowrap', display: 'block',
              }}
            />
          )}
        </div>

        {/* Next CTA — compact label so it never overlaps the price */}
        <button
          onClick={onNext}
          className="flex-1 flex items-center justify-center gap-1.5"
          style={{
            background: DA.goldMain,
            color: DA.paper,
            padding: '10px 12px',
            borderRadius: 12,
            fontFamily: DA.mono, fontSize: 10.5, letterSpacing: '0.18em',
            textTransform: 'uppercase', fontWeight: 800,
            minWidth: 0,
          }}
          data-testid="sticky-next"
        >
          <span className="truncate">{nextLabel}</span>
          <ChevronRight size={14} strokeWidth={2.6} style={{ flexShrink: 0 }} />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// HELP CTA — floating help / WhatsApp fallback
// ─────────────────────────────────────────────────────────────────────
function HelpCTA() {
  return (
    <div className="mt-14 text-center" data-testid="config-help">
      <SabrilDivider tagline="Vous hésitez ?" size={18} />
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 mt-4 px-6 py-3"
        style={{
          background: DA.paper,
          border: `0.5px solid ${DA.goldMain}`,
          borderRadius: 100,
          fontFamily: DA.sans, fontSize: 13.5, fontWeight: 700,
          color: DA.greenDeep,
          boxShadow: '0 8px 20px -10px rgba(11,43,36,0.18)',
        }}
        data-testid="help-whatsapp"
      >
        <MessageCircle size={16} strokeWidth={2} style={{ color: DA.goldMain }} />
        Je ne sais pas quoi choisir · RDV 30min WhatsApp
      </a>
      <p style={{
        fontFamily: DA.sans, fontSize: 11.5, color: DA.inkFaint,
        marginTop: 8, fontStyle: 'italic',
      }}>
        Nous définissons ensemble les chantiers prioritaires pour votre restaurant.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// UI PRIMITIVES
// ─────────────────────────────────────────────────────────────────────
function SectionHead({ overline, title, subtitle }) {
  return (
    <div>
      <p style={{
        fontFamily: DA.mono, fontSize: 10, letterSpacing: '0.32em',
        textTransform: 'uppercase', color: DA.goldMain, fontWeight: 800, marginBottom: 10,
      }}>
        {overline}
      </p>
      <h2 style={{
        fontFamily: DA.serif, fontSize: 26, fontWeight: 900,
        color: DA.greenDeep, letterSpacing: '-0.024em', lineHeight: 1.1,
      }}>
        {title}
      </h2>
      {subtitle && (
        <p style={{
          fontFamily: DA.sans, fontSize: 13, color: DA.inkMuted,
          lineHeight: 1.55, marginTop: 10,
        }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

function PrimaryButton({ onClick, disabled, testid, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-full flex items-center justify-center gap-2 transition-all"
      style={{
        background: disabled ? DA.inkFaint : DA.greenDeep,
        color: DA.ivory,
        padding: '16px 24px',
        borderRadius: 14,
        fontFamily: DA.mono, fontSize: 12, letterSpacing: '0.22em',
        textTransform: 'uppercase', fontWeight: 800,
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        boxShadow: disabled ? 'none' : '0 12px 28px -12px rgba(11,43,36,0.35)',
      }}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function SecondaryButton({ onClick, disabled, testid, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-full flex items-center justify-center gap-2 transition-all"
      style={{
        background: DA.paper,
        color: DA.greenDeep,
        padding: '15px 24px',
        borderRadius: 14,
        border: `1px solid ${DA.goldMain}`,
        fontFamily: DA.mono, fontSize: 12, letterSpacing: '0.22em',
        textTransform: 'uppercase', fontWeight: 800,
        opacity: disabled ? 0.55 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function Input({ label, value, onChange, type = 'text', testid, placeholder, multiline }) {
  const Tag = multiline ? 'textarea' : 'input';
  return (
    <label className={`block ${multiline ? 'sm:col-span-2 mt-3' : ''}`}>
      <span style={{
        fontFamily: DA.mono, fontSize: 9.5, letterSpacing: '0.22em',
        textTransform: 'uppercase', color: DA.inkMuted, fontWeight: 700,
        display: 'block', marginBottom: 6,
      }}>
        {label}
      </span>
      <Tag
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={multiline ? 3 : undefined}
        data-testid={testid}
        className="w-full transition-all"
        style={{
          background: DA.paper,
          border: `0.5px solid ${DA.goldSoft}`,
          borderRadius: 10,
          padding: '11px 14px',
          fontFamily: DA.sans, fontSize: 13.5, color: DA.greenDeep,
          outline: 'none',
          resize: multiline ? 'vertical' : 'none',
        }}
        onFocus={(e) => { e.target.style.borderColor = DA.goldMain; }}
        onBlur={(e) => { e.target.style.borderColor = DA.goldSoft; }}
      />
    </label>
  );
}

// Simple fade animation
const style = document.createElement('style');
style.textContent = `
  @keyframes fadein-config { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
  .animate-fadein { animation: fadein-config 320ms cubic-bezier(0.32, 0.72, 0, 1); }
`;
if (typeof document !== 'undefined' && !document.getElementById('config-fadein-style')) {
  style.id = 'config-fadein-style';
  document.head.appendChild(style);
}
